import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Building,
  ArrowRight,
  Package,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { organizationsAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import { useTheme } from "../contexts/ThemeContext";
import OrganizationModal from "../components/ui/OrganizationModal";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useAuth } from "../App";

const OrganizationsPage = () => {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const navigate = useNavigate();
  const {
    organizations,
    addOrganization,
    updateOrganization,
    removeOrganization,
  } = useApp();

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    organization: null,
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const { user } = useAuth();
  const [userRole, setUserRole] = useState("");

  const getUserRole = () => {
    try {
      if (user?.session) {
        const idToken = user.session.getIdToken();
        const payload = idToken.payload;
        const groups = payload["cognito:groups"] || [];

        if (groups.includes("SuperAdmins")) return "super_admin";
        if (groups.includes("OrganizationManager"))
          return "organization_manager";
        if (groups.includes("Trainers")) return "trainer";
        if (groups.includes("Students")) return "student";
      }
      return "student";
    } catch (error) {
      console.error("Error getting user role:", error);
      return "student";
    }
  };

  useEffect(() => {
    setUserRole(getUserRole());
  }, []);

  const handleCreate = async (organizationData) => {
    try {
      setLoading(true);
      const response = await organizationsAPI.create(organizationData);
      addOrganization(response.data.data);
      setIsModalOpen(false);
      setError(null);
      setSuccessMessage({
        type: "success",
        title: t("organizationsPage.notifications.createdTitle"),
        message: t("organizationsPage.notifications.createdMessage", {
          name: organizationData.name,
        }),
      });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error creating organization:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to create organization",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (organizationData) => {
    try {
      setLoading(true);
      const response = await organizationsAPI.update(
        editingOrganization.id,
        organizationData,
      );
      updateOrganization(response.data.data);
      setIsModalOpen(false);
      setEditingOrganization(null);
      setError(null);
      setSuccessMessage({
        type: "success",
        title: t("organizationsPage.notifications.updatedTitle"),
        message: t("organizationsPage.notifications.updatedMessage", {
          name: organizationData.name,
        }),
      });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error updating organization:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to update organization",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await organizationsAPI.delete(deleteConfirm.organization.id);
      removeOrganization(deleteConfirm.organization.id);
      setDeleteConfirm({ isOpen: false, organization: null });
      setError(null);
      setSuccessMessage({
        type: "success",
        title: t("organizationsPage.notifications.deletedTitle"),
        message: t("organizationsPage.notifications.deletedMessage", {
          name: deleteConfirm.organization.name,
        }),
      });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error deleting organization:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to delete organization",
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (organization) => {
    setEditingOrganization(organization);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingOrganization(null);
    setIsModalOpen(true);
  };

  const openDeleteConfirm = (organization) => {
    setDeleteConfirm({ isOpen: true, organization });
  };

  const navigateToDetails = (organization) => {
    navigate(`/organizations/${organization.id}`);
  };

  const filteredOrganizations = organizations.filter(
    (org) =>
      org.id !== "all" &&
      (org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.phone.includes(searchTerm)),
  );

  const totalPages = Math.ceil(filteredOrganizations.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedOrganizations = filteredOrganizations.slice(
    startIndex,
    startIndex + rowsPerPage,
  );

  const SuccessNotification = ({ successMessage, onClose }) => {
    if (!successMessage) return null;

    const isWarning = successMessage.type === "warning";
    const bgColor = isWarning
      ? "bg-gradient-to-r from-yellow-900/50 to-yellow-800/50"
      : "bg-gradient-to-r from-green-900/50 to-green-800/50";
    const borderColor = isWarning ? "border-yellow-600" : "border-green-600";
    const iconColor = isWarning ? "text-yellow-400" : "text-green-400";

    return (
      <div
        className={`mb-6 p-4 ${bgColor} ${borderColor} rounded-2xl shadow-sm backdrop-blur-sm border`}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {isWarning ? (
              <AlertTriangle className={`w-5 h-5 ${iconColor}`} />
            ) : (
              <CheckCircle className={`w-5 h-5 ${iconColor}`} />
            )}
          </div>
          <div className="ml-3 flex-1">
            <h3
              className={`text-sm font-semibold ${isWarning ? "text-yellow-200" : "text-green-200"}`}
            >
              {successMessage.title}
            </h3>
            <p
              className={`mt-1 text-sm ${isWarning ? "text-yellow-300" : "text-green-300"}`}
            >
              {successMessage.message}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`ml-3 ${isWarning ? "text-yellow-400 hover:text-yellow-200" : "text-green-400 hover:text-green-200"} hover:scale-110 transition-transform`}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`px-4 pb-8`}>
      <div
        className={`flex flex-col ${colors.cardBg} backdrop-blur-md bg-opacity-80 ${colors.cardBorder} border rounded-[2rem] shadow-xl relative transition-all duration-500`}
      >
        {/* Header */}
        <div
          className={`p-6 border-b ${colors.tableBorder} ${colors.tableHeader} rounded-t-[2rem]`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <h1
                className={`text-2xl font-black tracking-tight ${colors.textPrimary}`}
              >
                {t("organizationsPage.title")}
              </h1>
              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-xs text-white font-bold">
                  {filteredOrganizations.length}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => window.location.reload()}
                disabled={loading}
                className="btn-secondary flex items-center justify-center space-x-2"
                title={t("organizationsPage.refresh")}
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                <span className="hidden sm:inline">
                  {t("organizationsPage.refresh")}
                </span>
              </button>
              <button
                onClick={openCreateModal}
                disabled={loading}
                className="btn-primary flex items-center justify-center space-x-2"
                title={t("organizationsPage.addOrganization")}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {t("organizationsPage.addOrganization")}
                </span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md group">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.textMuted} group-focus-within:text-blue-500 transition-colors`}
            />
            <input
              type="text"
              placeholder={t("organizationsPage.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>

        {/* Messages */}
        <div className="px-6 pt-4 flex-shrink-0">
          <SuccessNotification
            successMessage={successMessage}
            onClose={() => setSuccessMessage(null)}
          />

          {error && (
            <div className="mb-6 p-4 bg-gradient-to-r from-red-900/50 to-red-800/50 border border-red-600 rounded-2xl shadow-sm backdrop-blur-sm">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <p className="text-red-300 font-medium">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="ml-3 text-red-400 hover:text-red-200"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Organizations Grid */}
        <div className="px-6 pb-10 pt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : paginatedOrganizations.length === 0 ? (
            <div className="text-center py-12">
              <div
                className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}
              >
                <Building className={`w-8 h-8 ${colors.emptyStateIcon}`} />
              </div>
              <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2`}>
                {searchTerm
                  ? t("organizationsPage.emptyState.titleSearch")
                  : t("organizationsPage.emptyState.title")}
              </h3>
              <p className={`${colors.textTertiary} mb-6`}>
                {searchTerm
                  ? t("organizationsPage.emptyState.messageSearch")
                  : t("organizationsPage.emptyState.message")}
              </p>
              {!searchTerm && (
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t("organizationsPage.addOrganization")}</span>
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {paginatedOrganizations.map((organization) => (
                <div
                  key={organization.id}
                  className={`${theme === "dark" ? "bg-white/5" : "bg-white"} rounded-2xl border ${theme === "dark" ? "border-white/10" : "border-slate-200"} shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group flex flex-col sm:flex-row items-start sm:items-center p-3 sm:p-4 gap-4 relative`}
                >
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    {/* Logo */}
                    {organization.logo ? (
                      <img
                        src={organization.logo}
                        alt={`${organization.name} logo`}
                        className={`w-12 h-12 sm:w-14 sm:h-14 object-contain rounded-xl border ${colors.border} bg-white p-1 shrink-0`}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shrink-0`}
                      >
                        <Building
                          className={`w-6 h-6 sm:w-7 sm:h-7 text-white`}
                        />
                      </div>
                    )}

                    {/* Name & Title */}
                    <div className="flex flex-col flex-1 min-w-0">
                      <h3
                        className={`text-base sm:text-lg font-bold ${colors.textPrimary} group-hover:text-blue-500 transition-colors truncate leading-tight`}
                      >
                        {organization.name}
                      </h3>
                      <div className="flex items-center mt-1">
                        <span
                          className={`text-[10px] sm:text-xs font-medium ${colors.textTertiary}`}
                        >
                          {new Date(
                            organization.createdAt,
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Middle: Details Strip (Address & Phone) */}
                  <div className="hidden md:flex flex-row items-center gap-6 flex-1 border-l border-gray-200 dark:border-white/10 pl-6 h-full">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span
                        className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5`}
                      >
                        {t("organizationsPage.card.address")}
                      </span>
                      <span
                        className={`text-[11px] sm:text-xs ${colors.textSecondary} truncate`}
                      >
                        {organization.address}
                      </span>
                    </div>
                    <div className="flex flex-col shrink-0 min-w-[120px]">
                      <span
                        className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5`}
                      >
                        {t("organizationsPage.card.phone")}
                      </span>
                      <span
                        className={`text-[11px] sm:text-xs ${colors.textSecondary}`}
                      >
                        {organization.phone}
                      </span>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center justify-end w-full sm:w-auto gap-2 sm:gap-3 shrink-0 mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-white/5">
                    <button
                      onClick={() => navigateToDetails(organization)}
                      className={`flex items-center justify-center space-x-1.5 px-3 py-2 sm:py-1.5 rounded-lg transition-all duration-200 font-bold text-[11px] sm:text-xs flex-1 sm:flex-auto
                         ${theme === "dark" ? "bg-white/10 hover:bg-white/20 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-800"}
                       `}
                    >
                      <Package className="w-3.5 h-3.5" />
                      <span>{t("organizationsPage.card.manage")}</span>
                    </button>

                    <div className="flex space-x-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEditModal(organization)}
                        disabled={loading}
                        className="p-2 sm:p-1.5 rounded-lg bg-orange-500/10 text-orange-500 hover:bg-orange-500 hover:text-white transition-all"
                      >
                        <Edit className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                      </button>
                      {userRole === "super_admin" && (
                        <button
                          onClick={() => openDeleteConfirm(organization)}
                          disabled={loading}
                          className="p-2 sm:p-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                        >
                          <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && paginatedOrganizations.length > 0 && (
          <div
            className={`px-4 sm:px-6 py-4 ${colors.tableHeader} border-t ${colors.tableBorder} rounded-b-[2rem]`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-4">
                <span className={`text-sm ${colors.paginationText}`}>
                  {t("common.itemsPerPage")}:
                </span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`${colors.borderSecondary} rounded-xl px-3 py-1.5 text-sm ${colors.paginationBg} ${colors.inputText} shadow-sm focus:outline-none focus:ring-2 ${colors.inputFocus}`}
                >
                  <option value={6}>6</option>
                  <option value={9}>9</option>
                  <option value={12}>12</option>
                </select>
              </div>

              <div className="flex items-center justify-between sm:justify-start space-x-4">
                <span className={`text-sm ${colors.paginationText}`}>
                  {startIndex + 1}-
                  {Math.min(
                    startIndex + rowsPerPage,
                    filteredOrganizations.length,
                  )}{" "}
                  of {filteredOrganizations.length}
                </span>
                <div
                  className={`flex space-x-2 ${colors.paginationBg} rounded-xl p-1 shadow-sm`}
                >
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`p-2 ${colors.textTertiary} ${colors.paginationHover} disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all duration-200`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className={`p-2 ${colors.textTertiary} ${colors.paginationHover} disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all duration-200`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <OrganizationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingOrganization(null);
        }}
        onSubmit={editingOrganization ? handleUpdate : handleCreate}
        organization={editingOrganization}
        title={
          editingOrganization
            ? t("organizationsPage.editOrgTitle")
            : t("organizationsPage.createOrgTitle")
        }
        loading={loading}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, organization: null })}
        onConfirm={handleDelete}
        title={t("organizationsPage.deleteOrgTitle")}
        message={t("organizationsPage.deleteOrgMessage", {
          name: deleteConfirm.organization?.name,
        })}
        confirmText={t("modals.confirmDelete")}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />
    </div>
  );
};

export default OrganizationsPage;
